// src/components/salidas/ReporteMensualMisiones.js
// Reporte mensual de Tesorería Misiones: mismo diseño que el de Tesorería General, pero con
// Misiones + Biblia + Especial en lugar de Diezmo + Pro-templo + Ofrenda. Archivo propio: no toca
// ReporteMensualTesoreria.js ni nada de Tesorería General.
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FaListUl, FaDownload, FaChevronDown, FaFileExcel, FaFilePdf } from "react-icons/fa";
import { AuthContext } from "../../context/AuthContext";
import Modal from "../Modal";

const API = "/api/reporte-misiones";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const DIAS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

// El mes en curso se toma con la hora de México (donde está la iglesia).
const mesActualMexico = () => {
  const partes = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "numeric",
  }).formatToParts(new Date());
  return {
    anio: Number(partes.find((p) => p.type === "year").value),
    mes: Number(partes.find((p) => p.type === "month").value),
  };
};

const numero = (valor) =>
  Number(valor).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Las columnas de ingreso quedan en blanco cuando no hay monto, como en la hoja de cálculo.
const celdaMonto = (valor) => (Number(valor) ? numero(valor) : "");

const etiquetaDia = (servicio) => {
  const nombre = DIAS[servicio.dia_semana];
  if (servicio.dia_semana === 0) {
    const hora = Number((servicio.hora || "0").split(":")[0]);
    return `${nombre} ${hora < 14 ? "Día" : "Tarde"}`;
  }
  return nombre;
};

const formatoFecha = (fecha) => {
  const [anio, mes, dia] = fecha.split("-");
  return `${dia}/${mes}/${anio}`;
};

const formatoMoneda = (valor) =>
  `$${Number(valor || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ReporteMensualMisiones() {
  const { token } = useContext(AuthContext);
  const inicial = mesActualMexico();

  const [anio, setAnio] = useState(inicial.anio);
  const [mes, setMes] = useState(inicial.mes);
  const [reporte, setReporte] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [verSalidas, setVerSalidas] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState("");
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuAbierto) return undefined;
    const cerrarSiClicAfuera = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuAbierto(false);
    };
    document.addEventListener("mousedown", cerrarSiClicAfuera);
    return () => document.removeEventListener("mousedown", cerrarSiClicAfuera);
  }, [menuAbierto]);

  // tipo: "mensual" (el mes elegido) o "anual" (todo el año elegido); formato: "excel" o "pdf".
  const descargar = async (tipo, formato) => {
    setMenuAbierto(false);
    setErrorDescarga("");
    setDescargando(true);
    try {
      const ruta =
        tipo === "mensual"
          ? `mensual/${formato}?anio=${anio}&mes=${mes}`
          : `anual/${formato}?anio=${anio}`;
      const res = await fetch(`${API}/${ruta}`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const datos = await res.json().catch(() => ({}));
        throw new Error(datos.mensaje || "No se pudo generar el archivo");
      }
      const blob = await res.blob();
      const base = tipo === "mensual" ? `reporte_tesoreria_misiones_${MESES[mes - 1].toLowerCase()}_${anio}` : `resumen_tesoreria_misiones_${anio}`;
      const url = URL.createObjectURL(blob);
      const enlace = document.createElement("a");
      enlace.href = url;
      enlace.download = `${base}.${formato === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(enlace);
      enlace.click();
      document.body.removeChild(enlace);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error descargando el reporte:", e);
      setErrorDescarga(e.message || "No se pudo generar el archivo");
    } finally {
      setDescargando(false);
    }
  };

  const cargarReporte = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/mensual?anio=${anio}&mes=${mes}`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setReporte(null);
        setError(data.mensaje || "No se pudo cargar el reporte");
        return;
      }
      setReporte(data);
    } catch (e) {
      console.error("Error cargando el reporte mensual:", e);
      setReporte(null);
      setError("Error de conexión con el servidor");
    } finally {
      setLoading(false);
    }
  }, [anio, mes, token]);

  useEffect(() => {
    cargarReporte();
  }, [cargarReporte]);

  const servicios = reporte?.servicios || [];
  const nombreMes = MESES[mes - 1];
  const totalMisiones = servicios.reduce((s, x) => s + Number(x.misiones || 0), 0);
  const totalBiblia = servicios.reduce((s, x) => s + Number(x.biblia || 0), 0);
  const totalEspecial = servicios.reduce((s, x) => s + Number(x.especial || 0), 0);

  const th = "px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-slate-800 border border-slate-400 bg-sky-200";
  const tdEtiqueta = "px-3 py-1.5 text-sm font-bold text-slate-900 border border-slate-400 bg-yellow-200 whitespace-nowrap";
  const tdMonto = "px-3 py-1.5 text-sm text-right tabular-nums text-slate-800 border border-slate-300";

  return (
    <div>
      {/* FILTROS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Mes</label>
          <select
            value={mes}
            onChange={(e) => setMes(Number(e.target.value))}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            {MESES.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Año</label>
          <input
            type="number"
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
            className="w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setVerSalidas(true)}
            disabled={!reporte || loading}
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
          >
            <FaListUl className="text-xs" /> Detalle de salidas
          </button>

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuAbierto((abierto) => !abierto)}
              disabled={descargando}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition disabled:opacity-60"
            >
              <FaDownload className="text-xs" /> {descargando ? "Generando..." : "Descargar"}
              <FaChevronDown className="text-[10px]" />
            </button>

            {menuAbierto && (
              <div className="absolute right-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Reporte de {nombreMes} {anio}
                </p>
                <button type="button" onClick={() => descargar("mensual", "excel")} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <FaFileExcel className="text-emerald-600" /> Excel (.xlsx)
                </button>
                <button type="button" onClick={() => descargar("mensual", "pdf")} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <FaFilePdf className="text-rose-600" /> PDF
                </button>
                <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 border-t border-slate-100 mt-1">
                  Resumen del año {anio}
                </p>
                <button type="button" onClick={() => descargar("anual", "excel")} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <FaFileExcel className="text-emerald-600" /> Excel (.xlsx)
                </button>
                <button type="button" onClick={() => descargar("anual", "pdf")} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <FaFilePdf className="text-rose-600" /> PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {errorDescarga && (
        <p className="text-xs text-rose-600 -mt-2 mb-3">{errorDescarga}</p>
      )}

      {/* REPORTE */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th colSpan={10} className="px-3 py-2 text-center text-sm font-bold uppercase tracking-wide text-slate-800 border border-slate-400 bg-sky-300">
                  Detalle Tesorería Misiones
                </th>
              </tr>
              <tr>
                <th className={th}>Mes</th>
                <th className={th}>Día</th>
                <th className={th}>Fecha</th>
                <th className={th}>Misiones</th>
                <th className={th}>Biblia</th>
                <th className={th}>Especial</th>
                <th className={th}>Entrada día</th>
                <th className={th}>Entrada mensual</th>
                <th className={th}>Subtotal</th>
                <th className={th}>Total</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-sm text-slate-400">Cargando...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-sm text-rose-500">{error}</td>
                </tr>
              ) : servicios.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-sm text-slate-400">
                    No hay servicios registrados para {nombreMes} {anio}.
                  </td>
                </tr>
              ) : (
                <>
                {servicios.map((s, i) => (
                  <tr key={s.id_servicio}>
                    {i === 0 && (
                      <td
                        rowSpan={servicios.length}
                        className="px-2 text-center font-bold text-slate-900 border border-slate-400 bg-yellow-200 align-middle"
                      >
                        {nombreMes.toUpperCase().split("").map((letra, j) => (
                          <div key={j} className="leading-tight">{letra}</div>
                        ))}
                      </td>
                    )}
                    <td className={tdEtiqueta}>{etiquetaDia(s)}</td>
                    <td className={tdEtiqueta}>{formatoFecha(s.fecha)}</td>
                    <td className={tdMonto}>{celdaMonto(s.misiones)}</td>
                    <td className={tdMonto}>{celdaMonto(s.biblia)}</td>
                    <td className={tdMonto}>{celdaMonto(s.especial)}</td>
                    <td className={`${tdMonto} font-medium`}>{numero(s.entradaDia)}</td>
                    {i === 0 && (
                      <>
                        <td rowSpan={servicios.length} className="px-4 text-center text-base font-medium text-slate-800 border border-slate-400 align-middle">
                          {numero(reporte.entradaMensual)}
                        </td>
                        <td rowSpan={servicios.length} className="px-4 text-center border border-slate-400 align-middle">
                          <div className={`text-base font-medium ${reporte.subtotal < 0 ? "text-rose-600" : "text-slate-800"}`}>
                            {numero(reporte.subtotal)}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                            Salidas del mes:<br />{numero(reporte.salidasMes)}
                          </div>
                        </td>
                        <td rowSpan={servicios.length} className="px-4 text-center border border-slate-400 align-middle">
                          <div className={`text-2xl font-bold underline ${reporte.total < 0 ? "text-rose-600" : "text-slate-900"}`}>{numero(reporte.total)}</div>
                          <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                            Mes anterior:<br />
                            <span className={reporte.totalAnterior < 0 ? "text-rose-600" : ""}>{numero(reporte.totalAnterior)}</span>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                <tr className="bg-slate-100 font-semibold">
                  <td colSpan={3} className="px-3 py-2 text-right text-sm text-slate-700 border border-slate-400">
                    Total del mes
                  </td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums text-slate-900 border border-slate-400">{numero(totalMisiones)}</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums text-slate-900 border border-slate-400">{numero(totalBiblia)}</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums text-slate-900 border border-slate-400">{numero(totalEspecial)}</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums text-slate-900 border border-slate-400">{numero(totalMisiones + totalBiblia + totalEspecial)}</td>
                  <td colSpan={3} className="border border-slate-400 bg-slate-50"></td>
                </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-slate-400 mt-3">
        Entrada día = Misiones + Biblia + Especial. Subtotal = entrada mensual − salidas del mes.
        Total = subtotal + total del mes anterior.
      </p>

      {/* DETALLE DE SALIDAS (DEDUCCIONES) DEL MES */}
      <Modal
        open={verSalidas}
        onClose={() => setVerSalidas(false)}
        title={`Salidas de ${nombreMes} ${anio}`}
        maxWidth="max-w-xl"
      >
        {(reporte?.detalleSalidas || []).length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">
            No hay salidas registradas en {nombreMes} {anio}.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2 pr-3 text-left font-semibold">Fecha</th>
                <th className="py-2 pr-3 text-left font-semibold">Concepto</th>
                <th className="py-2 pr-3 text-left font-semibold">Categoría</th>
                <th className="py-2 text-right font-semibold">Monto</th>
              </tr>
            </thead>
            <tbody>
              {reporte.detalleSalidas.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{formatoFecha(s.fecha)}</td>
                  <td className="py-2 pr-3 text-slate-800">{s.concepto}</td>
                  <td className="py-2 pr-3 text-slate-500">{s.categoria || "Sin categoría"}</td>
                  <td className="py-2 text-right font-medium text-slate-800 tabular-nums whitespace-nowrap">
                    {formatoMoneda(s.cantidad)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300">
                <td colSpan={3} className="py-3 pr-3 text-right text-sm font-semibold text-slate-700">
                  Total de deducciones del mes
                </td>
                <td className="py-3 text-right text-base font-bold text-slate-900 tabular-nums whitespace-nowrap">
                  {formatoMoneda(reporte.salidasMes)}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </Modal>
    </div>
  );
}
